import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useState,
} from 'react';

import { Button, Input, newAsyncDataEnvelope } from '@dr.pogodin/react-utils';

import Table from 'components/Table';
import { AdminPage } from 'components/admin';
import AddNewButton from 'components/admin/AddNewButton';
import MessageModal, { type IconT } from 'components/admin/MessageModal';
import { useArtistList } from 'hooks';
import { deleteArtist } from 'services/artists';

import {
  type AddNewPractitionerModalT,
  type ArtistT,
  P_ADMIN_ADD_NEW_PRACTITIONER,
  getId,
  useGlobalState,
  useGlobalStateObject,
} from 'utils';

import IconFilters from 'assets/images/icon-filters-24px.svg';

import IconSearch from 'assets/images/icon-search-24px.svg';

import secondaryButtonTheme from 'themes/Button/admin/secondary.scss';

import { FiltersPanel, type FiltersT, countFilters } from './FiltersPanel';
import Item from './Item';

import inputTheme from './input.scss';
import S from './style.scss';

const ICONS: Record<AddNewPractitionerModalT, IconT> = {
  ALREADY_PRACTITIONER: 'OK',
  CREATED_NEW: 'OK',
  ERROR: 'FAIL',
  PROMOTED_EXISTING: 'OK',
};

const MODAL_CONTENTS: Record<AddNewPractitionerModalT, ReactNode> = {
  ALREADY_PRACTITIONER: (
    <>
      <h1>User is already a practitioner</h1>
      <p>The user with given email is already a practitioner.</p>
    </>
  ),
  CREATED_NEW: (
    <>
      <h1>New practitioner created!</h1>
      <p>
        An invitation has been sent. Practitioner would be in
        “<strong>Pending&nbsp;Verification</strong>” status until
        the invitation is accepted.
      </p>
    </>
  ),
  ERROR: (
    <>
      <h1>Failed to create practitioner</h1>
      <p>Failed to create practitioner</p>
    </>
  ),
  PROMOTED_EXISTING: (
    <>
      <h1>User is promoted to practitioner</h1>
      <p>
        A user with the given email is already registered,
        and it is now promoted to practitioner.
      </p>
    </>
  ),
};

const CmsPractitionersPage: FunctionComponent = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [textSearch, setTextSearch] = useState<string>('');
  const [textSearchInput, setTextSearchInput] = useState<string>('');
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<FiltersT>({});

  const numFilters = countFilters(filters);
  const filtersCountNode: ReactNode = numFilters ? (
    <div styleName="numFilters">
      {numFilters}
    </div>
  ) : null;

  const artists = useArtistList({
    pageSize,
    singlePage: page,
    status: filters.status,
    textSearch,
  });

  useEffect(() => {
    if (artists.numPages !== undefined && page >= artists.numPages) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPage(Math.max(0, artists.numPages - 1));
    }
  }, [artists.numPages, page]);

  const gs = useGlobalStateObject();

  // Filter practitioners by modalities on the client side
  let filteredItems = artists.items;
  if (filters.modalities?.length || filters.customModalities?.length) {
    filteredItems = artists.items.filter((artist) => {
      // Check standard modalities
      if (filters.modalities?.length) {
        const hasModality = filters.modalities.some(
          (mod) => artist.modalities?.includes(mod),
        );
        if (!hasModality) return false;
      }

      // Check custom modalities
      if (filters.customModalities?.length) {
        const hasCustomModality = filters.customModalities.some(
          (mod) => artist.customModalities?.includes(mod),
        );
        if (!hasCustomModality) return false;
      }

      return true;
    });
  }

  const nodes: ReactNode[] = [];
  for (const artist of filteredItems) {
    const id = getId(artist);
    nodes.push(
      <Item
        artist={artist}
        key={id}
        onRemove={async () => {
          // TODO: Move it to a hook, similar to useSetArtist()
          // TODO: We should also remove "ARTIST" role from the user record!
          await deleteArtist(id);

          const list = {
            ...gs.get('artists.list'),
            operationId: '',
          };

          if (list.data) {
            const items: ArtistT[] = [];
            for (const item of list.data.items) {
              if (item.id !== id) items.push(item);
            }
            list.data = { ...list.data, items };
          }

          gs.set('artists.list', list);
          gs.set(`artists.map.${id}`, newAsyncDataEnvelope<ArtistT>());
          void artists.reload();
        }}
      />,
    );
  }

  const [modal, setModal] = useGlobalState('ui.addNewPractitioner.modal');

  return (
    <AdminPage
      contentClassName={S.container}
      subTitle="Track practitioners, assign tiers, manage profiles"
      title="Practitioners"
    >
      <div styleName="header">
        {/* TODO: It is the same field as in "shared/pages/admin/ContentManagement"
            it should to to a dedicated component!
         */}
        <div styleName="searchWrapper">
          <Input
            onBlur={() => {
              setTextSearchInput(textSearch);
            }}
            onChange={(e) => {
              setTextSearchInput(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setTextSearch(textSearchInput);
              }
            }}
            placeholder="Search"
            theme={inputTheme}
            value={textSearchInput}
          >
            <IconSearch styleName="searchIcon" />
          </Input>
        </div>
        <div styleName="filler" />
        <div styleName="actions">
          <AddNewButton
            hideLabelOnMobile
            label="Add New Practitioner"
            to={P_ADMIN_ADD_NEW_PRACTITIONER}
          />
          <Button
            onClick={() => {
              setOpenFilters(!openFilters);
            }}
            theme={secondaryButtonTheme}
          >
            <div styleName="iconButtonContent black">
              <IconFilters /> <span styleName="filterLabel">Filter {filtersCountNode}</span>
            </div>
          </Button>
        </div>
      </div>
      {openFilters ? (
        <FiltersPanel
          onChange={setFilters}
          onClose={() => {
            setOpenFilters(false);
          }}
          value={filters}
        />
      ) : null}
      <Table
        busy={artists.loading}
        numPages={artists.numPages ?? 0}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 50, 100]}
        setPage={setPage}
        setPageSize={setPageSize}
      >
        <thead>
          <tr>
            <th>Practitioners Name</th>
            <th>Practitioners ID</th>
            <th>Modalities</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>{nodes}</tbody>
      </Table>
      {modal ? (
        <MessageModal
          cancelButtonLabel="Understood"
          icon={ICONS[modal]}
          onCancel={() => {
            setModal(undefined);
          }}
        >
          {MODAL_CONTENTS[modal]}
        </MessageModal>
      ) : null}
    </AdminPage>
  );
};

export default CmsPractitionersPage;
